// @ts-strict-ignore
import {
  IconName,
  StatusValues,
} from '@usealloy/component-library';
import type { InvestigationStateTypes } from '@usealloy/typegen';
import {
  InvestigationStateTypesMap,
} from '@usealloy/typegen';

import { captureExceptionsToSentry, ResponseException } from '~/helpers/exceptionHandler';
import { snakeToCaps } from '~/helpers/formatting';
import { ENTITY_TYPES } from '~/types/entity';

import {
  HARDCODED_INV_ERRORS,
  INVESTIGATION_REVIEW_ALLOWED_OUTCOMES,
} from './constants';
import apiCalls from '~/helpers/apiCalls';
import type { InvestigationEntityMap } from './types';
import type { InvestigationDocumentResponse } from './redux/types';
import type { EntityForDisplay } from '~/documents/DocumentReview_v2/DocumentReviewModal';
import type { Document } from '~/entities/types';

type GetInvestigationBadgeInfo = (args: {
  investigationOutcome?: string;
  investigationState: InvestigationStateTypes;
}) => {
  icon: IconName;
  label: string;
  status: StatusValues;
} | null;

export const getInvestigationBadgeInfo: GetInvestigationBadgeInfo = ({
  investigationOutcome,
  investigationState,
}) => {
  if (investigationState === InvestigationStateTypesMap.ALERT_REVIEW) {
    return {
      icon: IconName.Review,
      label: snakeToCaps(investigationState),
      status: StatusValues.attention,
    };
  }
  if (investigationState === InvestigationStateTypesMap.CASE_REVIEW) {
    return {
      icon: IconName.Escalate,
      label: snakeToCaps(investigationState),
      status: StatusValues.warning,
    };
  }
  if (investigationOutcome === 'Confirmed Risk') {
    return {
      icon: IconName.ShieldExclamation,
      label: snakeToCaps(investigationOutcome),
      status: StatusValues.danger,
    };
  }
  if (investigationOutcome === 'Dismissed') {
    return {
      icon: IconName.CaseClosed,
      label: snakeToCaps(investigationOutcome),
      status: StatusValues.dismissed,
    };
  }
  // Should never really hit this case but best to have a fallback
  if (
    (
      investigationState === InvestigationStateTypesMap.ALERT_REVIEW_CLOSED
      || investigationState === InvestigationStateTypesMap.CASE_REVIEW_CLOSED
    )
    && !INVESTIGATION_REVIEW_ALLOWED_OUTCOMES.includes(investigationOutcome)
  ) {
    return {
      icon: IconName.CaseClosed,
      label: snakeToCaps(investigationState),
      status: StatusValues.neutral,
    };
  }
  return null;
};

export const getErrorMessage = (err: unknown): string => {
  if (err instanceof ResponseException && err.responseError?.message) {
    return err.responseError.message;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return 'Something went wrong. Please try again or contact support.';
};

export const investigationsErrorHandler = ({
  error,
  extra = {},
  message = null,
}) => {
  const { message: errorMessage } = error;
  // Filter out permissions or 404 errors from showing in Sentry
  if (
    errorMessage.includes('permission')
    || errorMessage.includes('could not be found')
    || HARDCODED_INV_ERRORS.has(errorMessage)
  ) {
    captureExceptionsToSentry(
      error,
      {
        ...extra,
        message,
      },
      'warning',
    );
  } else {
    captureExceptionsToSentry(
      error,
      {
        ...extra,
        message,
      },
    );
  }
};

export const pollDataExport = async ({
  exportToken,
  typhonDomain,
}) => {
  let exportResponse = await apiCalls.dataExports.getDataExportDetails({
    typhonDomain,
    exportToken,
  });
  let data = await exportResponse.json();
  let pollCount = 50;
  /* eslint-disable no-await-in-loop */
  while ((data.status === undefined || data.status === 'processing') && pollCount > 0) {
    pollCount -= 1;
    // eslint-disable-next-line no-promise-executor-return
    await new Promise(r => setTimeout(r, 3000));
    exportResponse = await apiCalls.dataExports.getDataExportDetails({
      typhonDomain,
      exportToken,
    });
    data = await exportResponse.json();
  }

  if (data.status === 'success') {
    return apiCalls.dataExports.downloadDataExport({
      typhonDomain,
      exportToken,
    });
  }
  // status => failure or polled enough number of times
  throw Error('CSV export failure');
};

export type OutcomeMeta = {
  escalated_investigation_status?: boolean;
  investigation_status?: boolean;
};

/**
 * Normalize and group investigation statuses with consistent ordering and sub-grouping.
 * - alert_review → base group (rank 0)
 * - case_review or escalated_investigation_status → subGroup 'Escalated' (rank 1)
 * - investigation_status → base group (rank 0)
 * - everything else → subGroup 'Outcomes' (rank 2)
 */
export const formatInvestigationStatuses = (
  statuses: string[],
  outcomeMetaByName: Map<string, OutcomeMeta>,
  options: {
    includeOutcomes?: boolean;
    baseLabel?: string;
    escalatedLabel?: string;
    outcomesLabel?: string;
  } = {},
): Array<{ value: string; subGroup?: string }> => {
  const {
    includeOutcomes = false,
    baseLabel = '',
    escalatedLabel = 'Escalated',
    outcomesLabel = 'Outcomes',
  } = options;

  const mapped = (statuses || []).map(status => {
    const originalStatus = status || '';
    // normalize strings
    const key = originalStatus.toLowerCase().trim().replace(/[_\s-]+/g, '_');
    if (key === 'alert_review') {
      return { value: originalStatus, rank: 0 };
    }
    if (key === 'case_review') {
      return { value: originalStatus, subGroup: escalatedLabel, rank: 1 };
    }
    const meta = outcomeMetaByName.get(key)
      || outcomeMetaByName.get(originalStatus.toLowerCase().trim());
    if (meta) {
      if (meta.escalated_investigation_status) {
        return { value: originalStatus, subGroup: escalatedLabel, rank: 1 };
      }
      if (meta.investigation_status) {
        return { value: originalStatus, rank: 0 };
      }
      // not a status → outcome
      return { value: originalStatus, subGroup: outcomesLabel, rank: 2 };
    }
    return { value: originalStatus, subGroup: outcomesLabel, rank: 2 };
  });

  const getSubGroupByRank = (rank: number): string => {
    if (rank === 0) return baseLabel;
    if (rank === 1) return escalatedLabel;
    return outcomesLabel;
  };

  const withLabels = mapped.map(item => ({
    ...item,
    subGroup: getSubGroupByRank(item.rank),
  }));

  const filtered = includeOutcomes
    ? withLabels
    : withLabels.filter(item => item.rank !== 2);

  return filtered
    .sort((a, b) => (a.rank - b.rank) || String(a.value).localeCompare(String(b.value)))
    .map(({ rank, ...rest }) => rest);
};

/**
 * Build a flat map of entities for document operations (upload, review).
 * For entity groups, extracts member entities from entityGroupRelationships.
 * For individual entities, uses them directly.
 * Entity groups themselves are excluded (we want the members, not the group).
 */
export const buildEntitiesMapForDocuments = (
  entities: InvestigationEntityMap | undefined,
): Record<string, EntityForDisplay> => (
  Object.entries(entities ?? {}).reduce((acc, [, entity]) => {
    if (entity.entityType === ENTITY_TYPES.entity_group && entity.entityGroupRelationships) {
      // Add member entities from the entity group
      entity.entityGroupRelationships.forEach(member => {
        acc[member.entityToken] = {
          entity_name: member.entityName || member.entityToken,
          entity_token: member.entityToken,
        };
      });
    } else if (entity.entityType !== ENTITY_TYPES.entity_group) {
      // Add individual entities directly
      acc[entity.entityToken] = {
        entity_name: entity.entityName || entity.entityToken,
        entity_token: entity.entityToken,
      };
    }
    return acc;
  }, {} as Record<string, EntityForDisplay>)
);

/**
 * Maps investigation document responses to the Document format expected by DocumentReviewModalV2.
 * Sorts documents with newest first (by created_at).
 */
export const mapInvestigationDocsForModal = (
  investigationDocuments: InvestigationDocumentResponse[],
): Document[] => [...investigationDocuments]
  .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
  .map(doc => ({
    ...doc,
    name: doc.filename,
    document_type: doc.type,
    timestamp: doc.created_at,
    review_url: '',
  })) as unknown as Document[];
